import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Loader2, Inbox, Mail, Building2, Tag, ArrowLeft, Search, LogOut,
} from "lucide-react";

export default function Inquiries() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await http.get("/inquiries");
        setItems(res.data);
      } catch (e) {
        setError("Could not load inquiries.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = items.filter((i) => {
    const s = q.toLowerCase();
    return (
      !s ||
      (i.name || "").toLowerCase().includes(s) ||
      (i.email || "").toLowerCase().includes(s) ||
      (i.company || "").toLowerCase().includes(s) ||
      (i.message || "").toLowerCase().includes(s) ||
      (i.project_type || "").toLowerCase().includes(s)
    );
  });

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-[#EFEBDE] pt-28 pb-24">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        {/* header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-[#16281C]/12 pb-8">
          <div>
            <Link to="/" data-testid="inquiries-back" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[#66766A] hover:text-[#2C5F3A] transition-colors">
              <ArrowLeft size={14} /> Back to site
            </Link>
            <h1 className="mt-5 font-display font-semibold text-4xl md:text-5xl tracking-tighter text-[#16281C] flex items-center gap-4">
              <Inbox size={36} className="text-[#2C5F3A]" strokeWidth={1.5} /> Inquiries
            </h1>
            <p className="mt-3 font-body text-[#4C5C50]">
              {items.length} total submission{items.length === 1 ? "" : "s"} · signed in as {user?.email}
            </p>
          </div>
          <button
            onClick={logout}
            data-testid="inquiries-logout"
            className="inline-flex items-center gap-2 border border-[#16281C]/20 text-[#16281C] font-mono text-[11px] tracking-[0.2em] uppercase px-5 py-3 hover:border-[#2C5F3A] hover:text-[#2C5F3A] transition-colors self-start"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>

        {/* search */}
        <div className="mt-8 relative max-w-md">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8B978C]" />
          <input
            data-testid="inquiries-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, company, message…"
            className="w-full bg-[#FAF8F1] border border-[#16281C]/15 pl-11 pr-4 py-3 font-body text-[15px] text-[#16281C] placeholder:text-[#8B978C] focus:outline-none focus:border-[#2C5F3A] transition-colors"
          />
        </div>

        {/* content */}
        <div className="mt-10">
          {loading ? (
            <div className="flex justify-center py-24">
              <Loader2 size={30} className="animate-spin text-[#2C5F3A]" />
            </div>
          ) : error ? (
            <p className="font-body text-[#4C5C50] py-16 text-center">{error}</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24">
              <Inbox size={40} className="text-[#8B978C] mx-auto" strokeWidth={1.25} />
              <p className="mt-5 font-body text-[#4C5C50]">
                {items.length === 0 ? "No inquiries yet." : "No results match your search."}
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {filtered.map((i) => (
                <div
                  key={i.id}
                  data-testid="inquiry-card"
                  className="bg-[#FAF8F1] border border-[#16281C]/12 p-6 md:p-8 hover:border-[#2C5F3A]/40 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display font-semibold text-xl md:text-2xl tracking-tight text-[#16281C]">
                        {i.name}
                      </h3>
                      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 font-mono text-[11px] tracking-[0.1em] uppercase text-[#66766A]">
                        <a href={`mailto:${i.email}`} className="inline-flex items-center gap-2 hover:text-[#2C5F3A]">
                          <Mail size={13} /> {i.email}
                        </a>
                        {i.company && (
                          <span className="inline-flex items-center gap-2"><Building2 size={13} /> {i.company}</span>
                        )}
                        {i.project_type && (
                          <span className="inline-flex items-center gap-2 text-[#2C5F3A]"><Tag size={13} /> {i.project_type}</span>
                        )}
                      </div>
                    </div>
                    <span className="shrink-0 font-mono text-[11px] tracking-[0.1em] uppercase text-[#8B978C]">
                      {fmtDate(i.created_at)}
                    </span>
                  </div>
                  <p className="mt-5 pt-5 border-t border-[#16281C]/12 font-body text-base text-[#2A3A2E] leading-relaxed whitespace-pre-wrap">
                    {i.message}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
